import { CircularProgress, Skeleton } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FullLogoDark, FullLogoLight } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { UspList } from 'shared/dialogs/purchase-premium';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useBrowserPrefersDarkMode } from 'shared/lib/mui';
import { PageEvents } from 'shared/services/tracking';
import { ID } from 'shared/types/id';

import { BillingUsps } from './billing-usps';
import * as Styled from './premium.style';
import { OnboardingRightPanelImage } from './right-panel';

const PREMIUM_IMAGE_URL = '/images/signup/illustration@3x.png';

type LoaderOption = {
  id: ID;
  translationLabel: string;
  selected?: boolean;
};

const loaderOptions: LoaderOption[] = [
  {
    id: 'year',
    translationLabel: 'dialogs.premium.payment-options.period.year',
    selected: true,
  },
  {
    id: 'month',
    translationLabel: 'dialogs.premium.payment-options.period.month',
  },
];

export type LoadingPaymentOptionsProps = {
  options?: LoaderOption[];
};

export const LoadingPaymentOptions: React.FC<LoadingPaymentOptionsProps> = ({
  options = loaderOptions,
}) => {
  const { t } = useTranslation();

  return (
    <Styled.List>
      {options.map(({ id, translationLabel, selected }) => (
        <Styled.ListItem key={id}>
          <Styled.OptionButton $selected={!!selected} disabled>
            <Styled.OptionHeader>
              <Typography variant="h6" component="span">
                {t(translationLabel)}
              </Typography>
            </Styled.OptionHeader>

            <Styled.OptionBody>
              <Typography variant="subtitle2" component="span">
                <Skeleton animation="wave" width="10.4rem" />
              </Typography>
            </Styled.OptionBody>
          </Styled.OptionButton>
        </Styled.ListItem>
      ))}
    </Styled.List>
  );
};

export type PremiumProps = {
  onSkip: () => void;
};

export const PremiumLoading: React.FC<PremiumProps> = ({ onSkip }) => {
  useTrackPageVisitEvent(PageEvents.OnboardingPremium, undefined);
  const prefersDarkMode = useBrowserPrefersDarkMode();
  const { t } = useTranslation();

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <Styled.Header>
          {prefersDarkMode ? (
            <FullLogoDark height="24" width="85" />
          ) : (
            <FullLogoLight height="24" width="85" />
          )}
        </Styled.Header>

        <Styled.LeftPanelContent>
          <Styled.Form>
            <Styled.Content>
              <Typography variant="h4" component="h1">
                {t('dialogs.premium.title')}
              </Typography>
              <Styled.Subtitle>{t('dialogs.premium.slogan')}</Styled.Subtitle>
            </Styled.Content>

            <UspList twoColumns />

            <Styled.PaymentOptions>
              <LoadingPaymentOptions />
            </Styled.PaymentOptions>

            <Styled.MobileBillingUsps>
              <BillingUsps />
            </Styled.MobileBillingUsps>
          </Styled.Form>

          <Styled.FooterArea>
            <Styled.ButtonsContainer>
              <Button
                type="button"
                form="tasks-form"
                size={Sizes.Large}
                disabled
                fullWidth
                alignCenter
                disabledBackgroundOnly
              >
                <CircularProgress color="primary" size={20} />
              </Button>
              <Button
                type="button"
                onClick={onSkip}
                variant={Variants.Text}
                size={Sizes.Medium}
                color={Colors.Inherit}
                fullWidth
                alignCenter
              >
                {t('forms.onboarding.skip.label')}
              </Button>
            </Styled.ButtonsContainer>

            <Styled.DesktopBillingUsps>
              <BillingUsps />
            </Styled.DesktopBillingUsps>
          </Styled.FooterArea>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>

      <Styled.RightPanelWrapper>
        <OnboardingRightPanelImage imageUrl={PREMIUM_IMAGE_URL} />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
